/**
 * 对照底图的本机存储。
 *
 * 用途：把一张纸质单据的扫描件或照片垫在编辑画布下面，照着它对齐标题、表格
 * 和签字位置 —— 「我有一张现成的表，照着做一份」是国内单据场景最常见的诉求。
 *
 * 为什么不存进模板：一张扫描件动辄几 MB。塞进模板会撑爆 localStorage
 * （模板里已经有印章和水印的 dataURL 了）、把云同步的 8MB 预算吃光，还会跟着
 * 模板被别人下载。所以按模板 id 单独存在本机，不进模板、不上云、不进导出。
 * 代价是换设备要重新传一次 —— 这个取舍和竞品一致，底图本来就是排版时的临时
 * 参照物，不是模板的一部分。
 */
const KEY = 'print_underlay_v1';

export interface Underlay {
  dataUrl: string;
  /** 0.1 ~ 1，越小越淡。 */
  opacity: number;
  /** 关掉就不垫，但图还留着，方便反复对照。 */
  visible: boolean;
}

type Store = Record<string, Underlay>;

function readStore(): Store {
  try {
    return JSON.parse(localStorage.getItem(KEY) || '{}') as Store;
  } catch {
    return {};
  }
}

export function loadUnderlay(templateId: string): Underlay | null {
  if (!templateId) return null;
  const u = readStore()[templateId];
  if (!u || typeof u.dataUrl !== 'string' || !u.dataUrl) return null;
  return {
    dataUrl: u.dataUrl,
    opacity: typeof u.opacity === 'number' ? u.opacity : 0.35,
    visible: u.visible !== false,
  };
}

/** 存一张底图。返回 false 表示本机空间不够（调用方要提示，别静默丢掉）。 */
export function saveUnderlay(templateId: string, u: Underlay | null): boolean {
  if (!templateId) return false;
  const store = readStore();
  if (u) store[templateId] = u;
  else delete store[templateId];
  try {
    localStorage.setItem(KEY, JSON.stringify(store));
    return true;
  } catch {
    return false;
  }
}
